import React, { } from 'react';
import { Luminary } from '../../types/luminary';
import { ACTION_TYPE_NONE } from '../../constants';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { AssetListItem } from '../../components/AssetListItem/AssetListItem';
import { Divider, Box, Typography, ListSubheader, Link } from '@mui/material';
import * as PXBColors from '@brightlayer-ui/colors';
import { HelpOutline } from '@mui/icons-material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { getRadioSignalIcon, getRadioSignalStrengthLabel, isWirelessRadioType, isWirelessRepeaterType, WirelessLuminaryType } from '../enum/ENUM';

type Props = {
    luminary: Luminary;
    deviceType?: string;
};



export const WirelessLuminaryInfo: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
    const { t } = useLanguageLocale();
    const luminary = props.luminary;
    const epasProductPageUrl = luminary.epasProductPageUrl?.trim() ?? '';
    const radioEpasProductPageUrl = luminary.radioEpasProductPageUrl?.trim() ?? '';

    const getNormalizedUrl = (productPageUrl: string): string => {
        if (/^https?:\/\//i.test(productPageUrl)) {
            return productPageUrl;
        }
        // Reject any explicit non-HTTP(S) protocol (e.g. javascript:, data:)
        if (/^[a-zA-Z][\w+\-.]*:/i.test(productPageUrl)) {
            return '';
        }
        return `https://${productPageUrl}`;
    };

    const renderEpasProductPageLink = (productPageUrl: string) => {
        const normalizedUrl = getNormalizedUrl(productPageUrl);
        return productPageUrl.length > 0 && normalizedUrl ? (
            <Link
                data-testid='luminary-info-product-information-link'
                href={normalizedUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.125,
                    backgroundColor: PXBColors.white[100],
                    cursor: 'pointer',
                }}
            >
                <OpenInNewOutlinedIcon sx={{ color: PXBColors.blue[500], fontSize: 16 }} />
                <Typography
                    variant="body2"
                    sx={{
                        color: PXBColors.blue[500],
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: 1.2,
                    }}
                >
                    {t('ASSETS_DETAILS.LUMINARY_PRODUCT_INFORMATION')}
                </Typography>
            </Link>
        ) : null;
    };
    const isRadioType = isWirelessRadioType(luminary.type);
    const isRepeaterType = isWirelessRepeaterType(luminary.type);
    // Default to native if type is empty or undefined
   const lumType =
    isRadioType
        ? WirelessLuminaryType.WIRELESS_RADIO
        : isRepeaterType
            ? WirelessLuminaryType.WIRELESS_REPEATER
            : WirelessLuminaryType.WIRELESS_NATIVE; // treat all other values as native
    const usesRadioDetails =
        lumType === WirelessLuminaryType.WIRELESS_RADIO ||
        lumType === WirelessLuminaryType.WIRELESS_REPEATER;
    const isNative = lumType === WirelessLuminaryType.WIRELESS_NATIVE;

    const gapColor = PXBColors.white[400];


    // Main Luminary Section (always shown)
    const renderLuminarySection = () => (
        <Box sx={{ borderRadius: 2, p: 1 }}>
            <AssetListItem
                data-testid='wireless-luminary-info-description'
                subtitle={luminary.luminaryName ?? '--'}
                title={t('LUMINARY_DETAILS.LUMINAIRE_NAME')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
            <AssetListItem
                data-testid='luminary-info-description'
                subtitle={luminary.luminaryDescription ?? '--'}
                title={t('LUMINARY_DETAILS.LUMINAIRE_DESCRIPTION')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
            <AssetListItem
                data-testid='luminary-info-zone'
                subtitle={luminary.zone ?? '--'}
                title={t('DEVICE_DETAILS.LUMINARY_ZONE')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
            <AssetListItem
                data-testid='luminary-info-address'
                subtitle={luminary.shortAddress ?? '--'}
                title={t('DEVICE_DETAILS.LUMINARY_SHORT_ADDRESS')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
            <AssetListItem
                data-testid='luminary-info-address'
                subtitle={luminary.numberOfNeighbors !== undefined && luminary.numberOfNeighbors !== null ? String(luminary.numberOfNeighbors) : '--'}
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {t('DEVICE_DETAILS.LUMINARY_NUMBER_OF_NEIGHBOURS')}
                        <HelpOutline style={{ color: PXBColors.gray[200], marginTop: 3 }} fontSize="small" sx={{ ml: 0.5 }} />
                    </Box>
                }
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
            <AssetListItem
                data-testid='luminary-radio-signal'
                 subtitle={getRadioSignalStrengthLabel(Number(luminary.radioSignal), t)}
                title={t('DEVICE_DETAILS.RADIO_SIGNAL')}
                imageType={<img src={getRadioSignalIcon(Number(luminary.radioSignal))} alt="signal" />}
                divider={false}
                titleBold={false}
            />
        </Box>
    );

    // "Luminaire" Section (header) - content depends on type
    const renderLuminaireHeaderSection = () => {
        // Repeater: do not render this section
        if (lumType === WirelessLuminaryType.WIRELESS_REPEATER) return null;

        return (
            <Box sx={{ borderRadius: 2, p: 1 }}>
                <ListSubheader
                    data-testid="luminaries-list-header"
                    id="luminaries-list-header"
                    style={{
                        display: 'flex',
                        fontWeight: 600,
                        fontSize: 14,
                        alignItems: 'center',
                        overflow: 'auto',
                        whiteSpace: 'nowrap',
                        height: 50,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        style={{
                            alignItems: 'left',
                            color: PXBColors.blue[500],
                            width: '92.3%',
                            paddingLeft: '10',
                        }}
                        id="luminaries-title"
                        data-testid="luminaries-title"
                    >
                        {t('DEVICE_DETAILS.LUMINAIRE')}
                    </Typography>
                </ListSubheader>
                <Divider sx={{ mb: 1 }} />
                {/* RADIO: Only CGLineId */}
                {lumType === WirelessLuminaryType.WIRELESS_RADIO && (
                    <AssetListItem
                        data-testid='luminary-info-CGLineId'
                        subtitle={luminary.cGLineId ?? '--'}
                        title={t('LUMINARY_DETAILS.CG_LINE_PLUS_ID')}
                        imageType={ACTION_TYPE_NONE}
                        divider={false}
                        titleBold={false}
                    />
                )}
                {/* NATIVE: Hide CGLineId, show rest */}
                {isNative && (
                    <>
                        <AssetListItem
                            data-testid='battery-type'
                            subtitle={luminary.luminaryPartNumber ?? '--'}
                            title={t('DEVICE_DETAILS.PART_NUMBER')}
                            imageType={ACTION_TYPE_NONE}
                            divider={false}
                            titleBold={false}
                        />
                        <AssetListItem
                            data-testid='luminary-info-CGLineId'
                            subtitle={luminary.luminaryEPAS}
                            title={t('DEVICE_DETAILS.EPAS_NUMBER')}
                            imageType={ACTION_TYPE_NONE}
                            divider={false}
                            titleBold={false}
                        />
                        <AssetListItem
                            data-testid='luminary-type'
                            subtitle={luminary.epasModelName ?? '--'}
                            title={t('LUMINARY_DETAILS.MODEL_NAME')}
                            imageType={ACTION_TYPE_NONE}
                            divider={false}
                            titleBold={false}
                        />
                        <AssetListItem
                            data-testid='luminary-type'
                            subtitle={luminary.epasShortDescription ?? '--'}
                            title={t('DEVICE_DETAILS.LUMINARY_MODEL_DESCRIPTION')}
                            imageType={ACTION_TYPE_NONE}
                            divider={false}
                            titleBold={false}
                        />
                        {renderEpasProductPageLink(epasProductPageUrl)}
                        <AssetListItem
                            data-testid='luminary-type'
                            subtitle={luminary.radioFirmwareVersion ?? '--'}
                            title={t('DEVICE_DETAILS.RADIO_FIRMWARE_VERSION')}
                            imageType={ACTION_TYPE_NONE}
                            divider={false}
                            titleBold={false}
                        />
                    </>
                )}
            </Box>
        );
    };

    // Battery Section (shown for native and radio)
    const renderBatteryBlock = () => (
        <Box sx={{ borderRadius: 2, p: 1 }}>
            <ListSubheader
                data-testid="luminaries-list-header"
                id="luminaries-list-header"
                style={{
                    display: 'flex',
                    fontWeight: 600,
                    fontSize: 14,
                    alignItems: 'center',
                    overflow: 'auto',
                    whiteSpace: 'nowrap',
                    height: 50,
                }}
            >
                <Typography
                    variant="subtitle1"
                    style={{
                        alignItems: 'left',
                        color: PXBColors.blue[500],
                        width: '92.3%',
                        paddingLeft: '10',
                    }}
                    id="luminaries-title"
                    data-testid="luminaries-title"
                >
                    {t('DEVICE_DETAILS.LUMINARY_BATTERY')}
                </Typography>
            </ListSubheader>
            <Divider sx={{ mb: 1 }} />
            <AssetListItem
                data-testid='battery-type'
                subtitle={'LUM16120'}
                title={t('DEVICE_DETAILS.PART_NUMBER')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
        </Box>
    );

    // Radio Section (shown for native and radio)
    const renderRadioBlock = () => (
        <Box sx={{ borderRadius: 2, p: 1 }}>
            <ListSubheader
                data-testid="luminaries-list-header"
                id="luminaries-list-header"
                style={{
                    display: 'flex',
                    fontWeight: 600,
                    fontSize: 14,
                    alignItems: 'center',
                    overflow: 'auto',
                    whiteSpace: 'nowrap',
                    height: 50,
                }}
            >
                <Typography
                    variant="subtitle1"
                    style={{
                        alignItems: 'left',
                        color: PXBColors.blue[500],
                        width: '92.3%',
                        paddingLeft: '10',
                    }}
                    id="luminaries-title"
                    data-testid="luminaries-title"
                >
                    {t('DEVICE_DETAILS.RADIO')}
                </Typography>
            </ListSubheader>
            <Divider sx={{ mb: 1 }} />
            <AssetListItem
                data-testid='battery-type'
                subtitle={luminary.radioPartNumber}
                title={t('DEVICE_DETAILS.PART_NUMBER')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
            <AssetListItem
                data-testid='luminary-info-radio-epas'
                subtitle={luminary.radioEPAS ?? "--"}
                title={t('DEVICE_DETAILS.EPAS_NUMBER')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
            {renderEpasProductPageLink(usesRadioDetails ? radioEpasProductPageUrl : epasProductPageUrl)}
            <AssetListItem
                data-testid='luminary-type'
                subtitle={usesRadioDetails ? luminary.radioModelName : luminary.epasModelName ?? "--"}
                title={t('LUMINARY_DETAILS.MODEL_NAME')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
            <AssetListItem
                data-testid='luminary-type'
                subtitle={usesRadioDetails ? luminary.radioModelDescription : luminary.epasShortDescription ?? "--"}
                title={t('DEVICE_DETAILS.LUMINARY_MODEL_DESCRIPTION')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
            <AssetListItem
                data-testid='luminary-type'
                subtitle={luminary.radioFirmwareVersion ?? '--'}
                title={t('DEVICE_DETAILS.RADIO_FIRMWARE_VERSION')}
                imageType={ACTION_TYPE_NONE}
                divider={false}
                titleBold={false}
            />
        </Box>
    );

    // Render logic
    return (
    <Box sx={{ flex: '1 1 0px', overflow: 'auto' }} data-testid="wireless-luminary-details-info-container">
        {/* Always show the main luminary section */}
        {renderLuminarySection()}

        {/* Shared details layout. */}
        <>
            <Divider />
            <Box sx={{ height: 16, background: gapColor }} />
            <Divider />
            {renderLuminaireHeaderSection()}
            <Divider />
            <Box sx={{ height: 16, background: gapColor }} />
            <Divider />
            {/* Not part of the story but we will need later*/}
            {/* {renderBatteryBlock()}
            <Divider /> */}
            {/* Radio block is rendered only for non-native luminaire types. */}
            {!isNative && (
                <>
                    <Box sx={{ height: 16, background: gapColor }} />
                    <Divider />
                    {renderRadioBlock()}
                    <Divider />
                </>
            )}
        </>
    </Box>
);
};
